import React, { useState, useEffect, useCallback } from 'react';
import { Container, Grid, Card, Alert, Button } from '@mui/material';
import PageTitleWrapper from 'src/components/PageTitleWrapper';
import PerksHeader from './PerksHeader';
import PerksContent from './PerksContent';
import { useAuth } from 'src/hooks/useAuth';
import { API } from '../../../config/config';
import Footer from 'src/components/Footer';

const Perks = () => {
  const { enhancedFetch } = useAuth();
  const [perksData, setPerksData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showError, setShowError] = useState(false);

  // Trying to fetch with useCallback now to test-run something
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    enhancedFetch(`${API.HOST}/api/v1/perks`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    })
      .then((data) => {
        setLoading(false);
        if (!data.error && Array.isArray(data)) {
          setPerksData(data);
        } else {
          setError(data?.error?.message || 'An error occurred, type is: ');
          console.log(data?.error?.message || 'An error occurred, type is: ', typeof data);
          setShowError(true);
        }
      })
      .catch((error) => {
        console.error('Error:', error);
        // Set error message based on error type
        if (
          error.name === 'TypeError' &&
          error.message.includes('Failed to fetch')
        ) {
          setError('Network error. Please check your internet connection');
        } else if (error.message) {
          setError(error.message);
        } else {
          setError('An unknown error occurred');
        }
        setShowError(true);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    fetchData().then();
  }, [fetchData]);

  const refreshPerks = useCallback(
    (updatedPerk: any) => {
      const url = `${API.HOST}/api/v1/perks`;
      setLoading(true);
      setError(null);
      enhancedFetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      })
        .then((data) => {
          // If an updated perk is provided, update it in the array
          if (updatedPerk) {
            const updatedIndex = data.findIndex(
              (perk) => perk.id === updatedPerk.id
            );
            if (updatedIndex !== -1) {
              data[updatedIndex] = updatedPerk; // Replace the old perk with the updated one
            }
          }
          setPerksData(data);
          setLoading(false);
        })
        .catch((error) => {
          console.error('Error refreshing perks:', error.message || error);
          // Set error message for refresh failures
          if (
            error.name === 'TypeError' &&
            error.message.includes('Failed to fetch')
          ) {
            setError('Network error while refreshing perks. Please try again');
          } else if (error.message) {
            setError(`Error refreshing perks: ${error.message}`);
          } else {
            setError('An unknown error occurred while refreshing perks');
          }
          setShowError(true);
          setLoading(false);
        });
    },
    [enhancedFetch, setPerksData]
  );

  const handleCloseError = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setShowError(false);
  };

  // Function to retry the data fetch
  const handleRetry = () => {
    fetchData();
  };

  // @ts-ignore
  return (
    <div>
      <PageTitleWrapper>
        <PerksHeader refreshPerks={() => refreshPerks(undefined)} />
      </PageTitleWrapper>
      <Container maxWidth="lg">
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={
              <Button color="inherit" size="small" onClick={handleRetry}>
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        )}
        <PerksContent
          perks={perksData}
          setPerksData={setPerksData}
          refreshPerks={refreshPerks}
          loading={loading}
          error={error}
        />
      </Container>
      <Footer />
    </div>
  );
};

export default Perks;
